import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { PosthogService } from 'src/shared/services/posthog.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

declare let gtag: Function;
declare let fbq: Function;
declare let _upf: any[];
declare const window: any;

/**
 * Hash a string using SHA-256 algorithm
 * Used for Facebook Advanced Matching which requires hashed PII
 */
async function sha256Hash(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

@Injectable()
export class Analytics {
  constructor(
    private posthogService: PosthogService,
    private storage: TidyStorage
  ) { }

  configureGoogleGtag (url: string, groupId: string, options) {
    if (!environment.production) {
      return;
    }
    gtag('js', new Date());

    gtag('config', environment.google_analytics_id, { 'page_path' : url });
    gtag('event', 'conversion', {'send_to': `${environment.google_analytics_id}/${groupId}`, ...options});
  }

  configureBing(event: string, eventCategory: string, eventLabel: string, eventValue: string) {
    if (!environment.production) {
      return;
    }
    window.uetq.push('event', event, {
      'event_category': eventCategory,
      'event_label': eventLabel,
      'event_value': eventValue
    });
  }

  configureFacebook(event: string, params?: object) {
    if (!environment.production) {
      return;
    }
    if (typeof fbq !== 'undefined') {
      fbq('track', event, params || {});
    }
  }

  /**
   * Track initiate_checkout conversion event across all platforms (Google, Facebook & PostHog)
   * Only fires in production environment
   * Supports enhanced conversions with user data (email, first name, last name, phone, address)
   * This method is non-blocking and will not throw errors
   */
  async trackInitiateCheckout(): Promise<void> {
    if (!environment.production) {
      return;
    }

    try {
      // Get user data for enhanced conversions
      const email = localStorage.getItem('customer_email');
      const firstName = localStorage.getItem('firstName');
      const lastName = localStorage.getItem('lastName');

      // Get phone number and address data from storage
      const phoneNumber = await this.storage.retrieve('phoneNumber');
      const addresses = await this.storage.retrieve('addresses');
      const firstAddress = addresses?.[0];

      // Build user data object with available fields
      const userData: any = {};
      if (email) {
        userData.email = email.toLowerCase().trim();
      }
      if (firstName) {
        userData.firstName = firstName.toLowerCase().trim();
      }
      if (lastName) {
        userData.lastName = lastName.toLowerCase().trim();
      }
      if (phoneNumber) {
        // Keep phone in E.164 format (e.g., +12125551234) as returned by the phone input component
        userData.phone = phoneNumber;
      }
      if (firstAddress?.zip) {
        userData.postalCode = firstAddress.zip;
      }
      if (firstAddress?.city) {
        userData.city = firstAddress.city.toLowerCase().trim();
      }
      if (firstAddress?.state) {
        userData.state = firstAddress.state.toLowerCase().trim();
      }
      if (firstAddress?.country_code) {
        userData.country = firstAddress.country_code.toLowerCase().trim();
      }

      // Google Enhanced Conversions - pass all available user data
      const googleUserData: any = {};
      if (userData.email) {
        googleUserData.email = userData.email;
      }
      if (userData.phone) {
        // Google expects E.164 format (e.g., +12125551234)
        googleUserData.phone_number = userData.phone;
      }
      googleUserData.address = {};
      if (userData.firstName) {
        googleUserData.address.first_name = userData.firstName;
      }
      if (userData.lastName) {
        googleUserData.address.last_name = userData.lastName;
      }
      if (userData.postalCode) {
        googleUserData.address.postal_code = userData.postalCode;
      }
      if (userData.city) {
        googleUserData.address.city = userData.city;
      }
      if (userData.state) {
        googleUserData.address.region = userData.state;
      }
      if (userData.country) {
        googleUserData.address.country = userData.country;
      }
      if (Object.keys(googleUserData.address).length === 0) {
        delete googleUserData.address;
      }
      const googleOptions = Object.keys(googleUserData).length > 0 ? { user_data: googleUserData } : {};
      this.configureGoogleGtag('initiate_checkout', '7n8-CIHWwbkBEIfH5s0D', googleOptions);

      // Facebook Advanced Matching - pass all available user data
      // Facebook requires: em (email), fn (first name), ln (last name), ph (phone), zp (zip), ct (city), st (state), country
      // All PII (personally identifiable information) must be: lowercase, hashed with SHA-256, and in E.164 format for phone
      const fbParams: any = {};
      if (userData.email) {
        fbParams.em = await sha256Hash(userData.email.toLowerCase());
      }
      if (userData.firstName) {
        fbParams.fn = await sha256Hash(userData.firstName.toLowerCase());
      }
      if (userData.lastName) {
        fbParams.ln = await sha256Hash(userData.lastName.toLowerCase());
      }
      if (userData.phone) {
        // Phone must be in E.164 format and hashed
        const phoneE164 = userData.phone.toLowerCase();
        fbParams.ph = await sha256Hash(phoneE164);
      }
      if (userData.postalCode) {
        fbParams.zp = await sha256Hash(userData.postalCode.toLowerCase());
      }
      if (userData.city) {
        fbParams.ct = await sha256Hash(userData.city.toLowerCase());
      }
      if (userData.state) {
        fbParams.st = await sha256Hash(userData.state.toLowerCase());
      }
      if (userData.country) {
        fbParams.country = userData.country.toLowerCase();
      }
      if (Object.keys(fbParams).length > 0) {
        this.configureFacebook('InitiateCheckout', fbParams);
      } else {
        this.configureFacebook('InitiateCheckout');
      }

      // PostHog event tracking - pass all available user data
      this.posthogService.capture('initiate_checkout', {
        email: userData.email,
        first_name: userData.firstName,
        last_name: userData.lastName,
        phone: userData.phone,
        postal_code: userData.postalCode,
        city: userData.city,
        state: userData.state,
        country: userData.country
      });
    } catch (error) {
      // Non-blocking: silently fail to avoid disrupting the user flow
      console.error('Analytics trackInitiateCheckout error:', error);
    }
  }

  trackUpfluenceSignup(customer: {
    customerId: number;
    firstName: string;
    lastName: string;
    email: string;
    accountType: string;
  }): void {
    if (typeof _upf !== 'undefined') {
      _upf.push([
        'order',
        {
          order_id: customer.customerId,
          order_name: `#SIGNUP-${customer.customerId}`,
          amount: 0,
          currency: 'USD',
          items: [{ name: `${customer.accountType} account signup`, amount: 0, currency: 'USD' }],
          customer: {
            customer_id: customer.customerId,
            first_name: customer.firstName,
            last_name: customer.lastName,
            email: customer.email,
          }
        }
      ]);
    }
  }
}
